<template>
    <div class="account-page signin-page">
        <div class="signin-stage">
            <section class="signin-hero" aria-label="Product overview">
                <span class="hero-eyebrow">Built for growing retailers</span>
                <h1 class="hero-headline">
                    Know your stock.<br />
                    Close every <em>sale.</em>
                </h1>
                <p class="hero-copy">
                    Manage branches, counters, purchases, GST billing, and live inventory
                    all from one company workspace that never loses the thread.
                </p>

                <div class="receipt-preview">
                    <div class="receipt-head">
                        <span>STOCKLEDGER</span>
                        <span>No. <strong>00248</strong> / TODAY</span>
                    </div>

                    <div class="receipt-title">
                        <div>
                            <span>Today at a glance</span>
                            <h2>Sales, stock &amp; branches in sync</h2>
                        </div>
                        <span class="live-pill">Live</span>
                    </div>

                    <div class="stat-row">
                        <span class="stat-label">Net sales</span>
                        <strong class="stat-value">Rs. 4.82L <small class="delta up">+18.4%</small></strong>
                    </div>
                    <div class="stat-row">
                        <span class="stat-label">Invoices raised</span>
                        <strong class="stat-value">248 <small class="delta up">+32</small></strong>
                    </div>
                    <div class="stat-row">
                        <span class="stat-label">Low stock items</span>
                        <strong class="stat-value">19 <small class="delta flag">review</small></strong>
                    </div>

                    <div class="barcode-row">
                        <div class="barcode" aria-hidden="true">
                            <span style="--w:2; --h:22"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:3; --h:26"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:2; --h:22"></span>
                            <span style="--w:4; --h:30"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:2; --h:22"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:3; --h:26"></span>
                            <span style="--w:2; --h:22"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:4; --h:30"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:2; --h:22"></span>
                            <span style="--w:3; --h:26"></span>
                            <span style="--w:1; --h:18"></span>
                            <span style="--w:2; --h:22"></span>
                        </div>
                        <span class="receipt-note">Tracked / Taxed / True</span>
                    </div>
                </div>
            </section>

            <section class="ticket-wrap" aria-label="Sign in form">
                <div class="signin-ticket">
                    <div class="login-userheading">
                        <span class="ticket-eyebrow">Company sign in</span>
                        <h2>Welcome back</h2>
                        <p>
                            Sign in to your company workspace and keep every branch,
                            invoice, and shelf in sync.
                        </p>
                    </div>

                    <Form
                        class="login"
                        @submit="onSubmit"
                        :validation-schema="schema"
                        v-slot="{ errors }"
                    >
                        <div class="form-login">
                            <label>Email address</label>
                            <div class="input-wrap">
                                <Field
                                    name="email"
                                    type="text"
                                    v-model="email"
                                    class="form-control"
                                    placeholder="name@company.com"
                                    :class="{ 'is-invalid': errors.email || apiErrors.email }"
                                />
                                <div class="invalid-feedback">{{ errors.email || apiErrors.email }}</div>
                            </div>
                        </div>

                        <div class="form-login">
                            <label>Password</label>
                            <div class="input-wrap">
                                <Field
                                    name="password"
                                    :type="passwordType"
                                    v-model="password"
                                    class="form-control pass-input"
                                    placeholder="Enter password"
                                    :class="{ 'is-invalid': errors.password || apiErrors.password }"
                                />
                                <button
                                    type="button"
                                    @click="toggleShow"
                                    class="password-toggle"
                                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                                >
                                    <span
                                        class="fas"
                                        :class="{
                                            'fa-eye': showPassword,
                                            'fa-eye-slash': !showPassword,
                                        }"
                                    ></span>
                                </button>
                                <div class="invalid-feedback">{{ errors.password || apiErrors.password }}</div>
                            </div>
                        </div>

                        <div v-if="apiErrors.general" class="alert alert-danger mb-3">
                            {{ apiErrors.general }}
                        </div>

                        <div class="form-login">
                            <button class="btn btn-login" type="submit" :disabled="isLoading">
                                {{ isLoading ? 'Signing in...' : 'Sign in' }}
                            </button>
                        </div>
                    </Form>

                    <div class="ticket-divider"><span>Why teams switch</span></div>

                    <div class="signin-assurance">
                        <span>Branch-aware access</span>
                        <span>GST ready</span>
                        <span>Live stock control</span>
                    </div>

                    <div class="mini-metrics">
                        <div>
                            <strong>2,400+</strong>
                            <span>Retailers onboard</span>
                        </div>
                        <div>
                            <strong>Rs. 38Cr</strong>
                            <span>Stock tracked / mo</span>
                        </div>
                        <div>
                            <strong>99.9%</strong>
                            <span>Uptime</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { mapActions } from 'vuex';
import { getDefaultRouteForAuth } from '@/auth/navigation';

export default {
    components: {
        Form,
        Field,
    },
    data() {
        return {
            email: this.$route.query.email || '',
            password: "",

            showPassword: false,
            isLoading: false,

            apiErrors: {
                email: null,
                password: null,
                general: null,
            },

            schema: Yup.object().shape({
                email: Yup.string().required("Email is required").email("Email is invalid"),
                password: Yup.string()
                    .min(6, "Password must be at least 6 characters")
                    .required("Password is required"),
            }),
        };
    },
    computed: {
        passwordType() {
            return this.showPassword ? "text" : "password";
        },
    },
    methods: {
        ...mapActions(['login']),

        toggleShow() {
            this.showPassword = !this.showPassword;
        },

        async onSubmit(values) {
            this.apiErrors = { email: null, password: null, general: null };
            this.isLoading = true;

            try {
                await this.login({
                    email: values.email,
                    password: values.password
                });

                this.$router.push(getDefaultRouteForAuth(this.$store.getters.authContext));

            } catch (err) {
                const responseData = err.response?.data?.detail ?? err.response?.data;
                const status = typeof responseData === 'object' ? responseData?.status : null;
                const message = typeof responseData === 'string'
                    ? responseData
                    : responseData?.message;

                if (status === 'PASSWORD_CHANGE_REQUIRED') {
                    this.apiErrors.general = message || 'You must change your password before logging in.';

                    this.$router.push({
                        name: 'change-password',
                        query: { email: values.email, force: true }
                    });

                } else if (status === 'FAILED') {
                    this.apiErrors.general = message || 'Invalid email or password.';
                } else if (typeof responseData === 'string') {
                    this.apiErrors.general = responseData;
                } else {
                    this.apiErrors.general = 'An unexpected error occurred. Check your network connection or try again later.';
                }

            } finally {
                this.isLoading = false;
            }
        },
    },

    created() {
        if (this.$store.getters.isAuthenticated) {
            this.$router.push(getDefaultRouteForAuth(this.$store.getters.authContext));
        }
    }
};
</script>

<style scoped>
.signin-page {
    min-height: 100vh;
    padding: 56px 24px;
    color: #241c15;
    background:
        radial-gradient(1200px 700px at 8% -10%, #fde9ce 0%, transparent 60%),
        radial-gradient(900px 600px at 105% 10%, #fbddbb 0%, transparent 55%),
        #fbf3e7;
    font-family: "Inter", "Segoe UI", sans-serif;
}

.signin-stage {
    display: grid;
    width: 100%;
    max-width: 1180px;
    min-height: calc(100vh - 112px);
    margin: 0 auto;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 56px;
    align-items: center;
}

.hero-eyebrow,
.receipt-head,
.receipt-title span,
.stat-row,
.receipt-note,
.ticket-eyebrow,
.ticket-divider span,
.mini-metrics strong {
    font-family: "JetBrains Mono", "Consolas", monospace;
}

.hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #c74a05;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.hero-eyebrow::before {
    width: 7px;
    height: 7px;
    content: "";
    background: #2f6b4f;
    border-radius: 50%;
}

.hero-headline,
.receipt-title h2,
.login-userheading h2 {
    font-family: Georgia, "Times New Roman", serif;
}

.hero-headline {
    margin: 22px 0 18px;
    color: #241c15;
    font-size: clamp(38px, 4.4vw, 58px);
    font-weight: 600;
    line-height: 1.04;
}

.hero-headline em {
    color: #c74a05;
    background: linear-gradient(180deg, transparent 62%, #ffb648 62%);
    font-style: normal;
}

.hero-copy {
    max-width: 460px;
    margin: 0 0 34px;
    color: #5b5044;
    font-size: 16.5px;
    line-height: 1.6;
}

.receipt-preview {
    position: relative;
    max-width: 480px;
    padding: 22px 26px 20px;
    background: #fffdf9;
    border: 1px solid #e2d1b7;
    border-radius: 14px;
    box-shadow: 0 30px 60px -25px rgba(36, 28, 21, 0.35);
}

.receipt-preview::before,
.receipt-preview::after {
    position: absolute;
    right: 0;
    left: 0;
    height: 14px;
    content: "";
    background:
        radial-gradient(circle at 12px 50%, transparent 7px, #fbf3e7 7.5px),
        radial-gradient(circle at 12px 50%, #fffdf9 6.5px, transparent 7px);
    background-repeat: repeat-x;
    background-size: 24px 14px;
}

.receipt-preview::before {
    top: -7px;
}

.receipt-preview::after {
    bottom: -7px;
    transform: scaleY(-1);
}

.receipt-head {
    display: flex;
    justify-content: space-between;
    padding-bottom: 12px;
    color: #5b5044;
    border-bottom: 1.5px dashed #e2d1b7;
    font-size: 11.5px;
    letter-spacing: 0.06em;
}

.receipt-head strong {
    color: #241c15;
}

.receipt-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 14px 0 12px;
}

.receipt-title span {
    color: #c74a05;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.receipt-title h2 {
    margin: 4px 0 0;
    color: #241c15;
    font-size: 19px;
    font-weight: 600;
}

.live-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    color: #fffdf9 !important;
    background: #2f6b4f;
    border-radius: 20px;
    font-size: 10.5px !important;
}

.live-pill::before {
    width: 6px;
    height: 6px;
    content: "";
    background: #7fe0a6;
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(127, 224, 166, 0.25);
}

.stat-row {
    display: grid;
    padding: 10px 0;
    grid-template-columns: 1fr auto;
    align-items: center;
    border-bottom: 1px dashed #e2d1b7;
}

.stat-row:last-of-type {
    border-bottom: 0;
}

.stat-label {
    color: #5b5044;
    font-size: 12.5px;
}

.stat-value {
    color: #241c15;
    font-size: 15px;
    text-align: right;
}

.delta {
    margin-left: 8px;
    font-size: 11px;
    font-weight: 700;
}

.delta.up {
    color: #2f6b4f;
}

.delta.flag {
    color: #c74a05;
}

.barcode-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 16px;
    padding-top: 14px;
}

.barcode {
    display: flex;
    height: 30px;
    align-items: flex-end;
    gap: 2px;
}

.barcode span {
    display: block;
    width: calc(var(--w) * 1px);
    height: calc(var(--h) * 1px);
    background: #241c15;
}

.receipt-note {
    color: #5b5044;
    font-size: 10px;
    letter-spacing: 0.12em;
    text-align: right;
    text-transform: uppercase;
}

.ticket-wrap {
    position: relative;
}

.signin-ticket {
    position: relative;
    padding: 40px 38px 32px;
    background: #fffdf9;
    border: 1px solid #e2d1b7;
    border-radius: 20px;
    box-shadow: 0 30px 60px -25px rgba(36, 28, 21, 0.35);
}

.signin-ticket::before,
.signin-ticket::after {
    position: absolute;
    top: 50%;
    width: 34px;
    height: 34px;
    content: "";
    background: #f4e6d2;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px #e2d1b7;
    transform: translateY(-50%);
}

.signin-ticket::before {
    left: -17px;
}

.signin-ticket::after {
    right: -17px;
}

.ticket-eyebrow {
    display: inline-block;
    padding: 6px 12px;
    color: #c74a05;
    background: #fde6d2;
    border-radius: 20px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.login-userheading h2 {
    margin: 16px 0 6px;
    color: #241c15;
    font-size: 30px;
    font-weight: 600;
}

.login-userheading p {
    margin: 0 0 26px;
    color: #5b5044;
    font-size: 14.5px;
    line-height: 1.55;
}

.form-login {
    margin-bottom: 18px;
}

.form-login label {
    display: block;
    margin-bottom: 7px;
    color: #241c15;
    font-size: 12.5px;
    font-weight: 700;
}

.input-wrap {
    position: relative;
}

.form-login input {
    width: 100%;
    height: 48px;
    padding: 0 42px 0 14px;
    color: #241c15;
    background: #fbf3e7;
    border: 1.5px solid #e2d1b7;
    border-radius: 9px;
    font-size: 14.5px;
    outline: none;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.form-login input::placeholder {
    color: #a6987f;
}

.form-login input:focus {
    background: #fffdf9;
    border-color: #f2600c;
    box-shadow: 0 0 0 4px rgba(242, 96, 12, 0.12);
}

.password-toggle {
    position: absolute;
    top: 24px;
    right: 10px;
    display: inline-flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #5b5044;
    background: transparent;
    border: 0;
    cursor: pointer;
    transform: translateY(-50%);
}

.password-toggle:focus-visible,
.btn-login:focus-visible {
    outline: 2px solid #c74a05;
    outline-offset: 2px;
}

.btn-login {
    width: 100%;
    height: 50px;
    padding: 0 18px;
    color: #fffdf9;
    background: linear-gradient(180deg, #f2600c 0%, #c74a05 100%);
    border: 0;
    border-radius: 10px;
    box-shadow: 0 10px 22px -8px rgba(199, 74, 5, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.25);
    font-size: 15px;
    font-weight: 800;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.btn-login:hover,
.btn-login:focus {
    color: #fffdf9;
    transform: translateY(-1px);
    box-shadow: 0 14px 26px -8px rgba(199, 74, 5, 0.6);
}

.btn-login:disabled {
    cursor: not-allowed;
    opacity: 0.72;
}

.ticket-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 26px 0 18px;
}

.ticket-divider::before,
.ticket-divider::after {
    height: 1px;
    content: "";
    background: #e2d1b7;
    flex: 1;
}

.ticket-divider span {
    color: #5b5044;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.signin-assurance {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.signin-assurance span {
    padding: 6px 11px;
    color: #2f6b4f;
    background: #e4eee7;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 700;
}

.mini-metrics {
    display: grid;
    margin-top: 22px;
    padding-top: 20px;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    border-top: 1px solid #e2d1b7;
}

.mini-metrics div {
    text-align: center;
}

.mini-metrics strong {
    display: block;
    color: #241c15;
    font-size: 16px;
}

.mini-metrics span {
    display: block;
    margin-top: 4px;
    color: #5b5044;
    font-size: 10.5px;
    line-height: 1.3;
}

.is-invalid {
    border-color: #dc3545 !important;
}

.invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    color: #dc3545;
    font-size: 0.875em;
}

@media (max-width: 900px) {
    .signin-page {
        padding: 36px 20px;
    }

    .signin-stage {
        min-height: auto;
        grid-template-columns: 1fr;
        gap: 34px;
    }

    .signin-ticket::before,
    .signin-ticket::after {
        display: none;
    }
}

@media (max-width: 575px) {
    .signin-page {
        padding: 28px 14px;
    }

    .hero-headline {
        font-size: 36px;
    }

    .receipt-preview,
    .signin-ticket {
        padding-right: 20px;
        padding-left: 20px;
    }

    .receipt-head,
    .receipt-title,
    .barcode-row {
        align-items: flex-start;
        flex-direction: column;
    }

    .stat-row,
    .mini-metrics {
        grid-template-columns: 1fr;
    }

    .stat-value,
    .receipt-note {
        text-align: left;
    }
}

@media (prefers-reduced-motion: reduce) {
    .btn-login {
        transition: none;
    }
}
</style>
